import * as React from 'react';
import { Component } from 'react';
import moment from 'moment';
import { SimpleModal } from '../SimpleModal/SimpleModal';
import { Subscription } from '../../models/subscription';
import { getSubscriptionStatement } from '../../helpers/service/admin/subscriptions';
import { AppUser } from 'Src/views/App/App';
import { DATE_FORMAT } from '../../consts/date';
import './SubscriptionReportSettingsModal.scss';

interface Props {
  isOpen: boolean;
  onCancelClick: () => void;
  subscription: Subscription;
  user: AppUser;
}

interface State {
  dateFrom: string;
  dateTo: string;
  isIncludeText: boolean;
  isDateFromInvalid: boolean;
  isDateToInvalid: boolean;
  isDownloading: boolean;
  errorMessage: string;
}

export class SubscriptionReportSettingsModal extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      dateFrom: moment(props.subscription.purchaseDate).format(DATE_FORMAT),
      dateTo: moment(new Date()).format(DATE_FORMAT),
      isIncludeText: false,
      isDateFromInvalid: false,
      isDateToInvalid: false,
      isDownloading: false,
      errorMessage: ''
    };
  }

  onDateFromChange = event => {
    this.setState({
      dateFrom: event.target.value,
      isDateFromInvalid: false,
      errorMessage: ''
    });
  };

  onDateToChange = event => {
    this.setState({
      dateTo: event.target.value,
      isDateToInvalid: false,
      errorMessage: ''
    });
  };

  onIncludeTextChange = () => {
    this.setState(prevState => ({
      isIncludeText: !prevState.isIncludeText
    }));
  };

  setQuickRange = (range: 'purchase' | '30' | 'term') => {
    const { subscription } = this.props;
    const today = moment();
    let dateFrom = moment(subscription.purchaseDate);

    if (range === '30') {
      dateFrom = moment().subtract(30, 'days');
    }

    if (range === 'term') {
      const month = today.month();
      const year = today.year();
      dateFrom = moment().year(month >= 8 ? year : year - 1).month(8).date(1).startOf('day');
    }

    this.setState({
      dateFrom: dateFrom.format(DATE_FORMAT),
      dateTo: today.format(DATE_FORMAT),
      isDateFromInvalid: false,
      isDateToInvalid: false,
      errorMessage: ''
    });
  };

  onDownloadClick = () => {
    if (!this.isFormDataValid()) {
      return;
    }

    const { user, subscription } = this.props;
    const { dateFrom, dateTo, isIncludeText } = this.state;
    const from = moment.utc(dateFrom, DATE_FORMAT).toDate();
    const to = moment.utc(dateTo, DATE_FORMAT).toDate();

    this.setState({ isDownloading: true, errorMessage: '' });

    getSubscriptionStatement(user, subscription.id, from, to, isIncludeText).then(response => {
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(file);
      const fileName = `Report_${user.activeSchool.name}_${subscription.phoneNumber}.pdf`;
      const link = document.createElement('a');
      link.setAttribute('href', fileUrl);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.props.onCancelClick();
    }).catch(() => {
      this.setState({
        isDownloading: false,
        errorMessage: 'The report could not be generated. Please try again or adjust the date range.'
      });
    });
  };

  isFormDataValid = (): boolean => {
    const { dateFrom, dateTo } = this.state;
    const isDateFromValid = moment(dateFrom, DATE_FORMAT, true).isValid();
    const isDateToValid = moment(dateTo, DATE_FORMAT, true).isValid();
    const isRangeValid = isDateFromValid && isDateToValid && !moment(dateFrom, DATE_FORMAT).isAfter(moment(dateTo, DATE_FORMAT));

    if (isRangeValid) {
      return true;
    }

    this.setState({
      isDateFromInvalid: !isDateFromValid || !isRangeValid,
      isDateToInvalid: !isDateToValid || !isRangeValid,
      errorMessage: ''
    });
    return false;
  };

  renderDateField(label: string, value: string, invalid: boolean, onChange: (event: any) => void): React.ReactNode {
    return (
      <label className="subscriptionReportDateField">
        <span className="subscriptionReportLabel">{label}</span>
        <span className={invalid ? 'subscriptionReportDateInput subscriptionReportDateInput--error' : 'subscriptionReportDateInput'}>
          <span className="subscriptionReportDateIcon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            </svg>
          </span>
          <input type="text" value={value} placeholder={DATE_FORMAT} onChange={onChange} />
        </span>
        {invalid && <span className="subscriptionReportError">Use {DATE_FORMAT} and keep From before To.</span>}
      </label>
    );
  }

  render() {
    const { isOpen, onCancelClick, subscription } = this.props;
    const { dateFrom, dateTo, isDateFromInvalid, isDateToInvalid, isIncludeText, isDownloading, errorMessage } = this.state;

    const body = (
      <div className="subscriptionReportModalBody">
        <div className="subscriptionReportSummary">
          <div>
            <div className="subscriptionReportEyebrow">Usage report</div>
            <div className="subscriptionReportPackage">{subscription.type || 'SMS bundle'}</div>
          </div>
          <div className="subscriptionReportSender">{subscription.phoneNumber}</div>
        </div>

        <div className="subscriptionReportQuickRanges">
          <button type="button" onClick={() => this.setQuickRange('purchase')}>Since purchase</button>
          <button type="button" onClick={() => this.setQuickRange('30')}>Last 30 days</button>
          <button type="button" onClick={() => this.setQuickRange('term')}>This school year</button>
        </div>

        <div className="subscriptionReportDateRange">
          {this.renderDateField('From', dateFrom, isDateFromInvalid, this.onDateFromChange)}
          <span className="subscriptionReportDateArrow" aria-hidden="true">→</span>
          {this.renderDateField('To', dateTo, isDateToInvalid, this.onDateToChange)}
        </div>

        {errorMessage && <div className="subscriptionReportServerError">{errorMessage}</div>}

        <label className="subscriptionReportCheckbox">
          <input type="checkbox" checked={isIncludeText} onChange={this.onIncludeTextChange} />
          <span>Include SMS text in the report</span>
        </label>
      </div>
    );

    return (
      <SimpleModal
        isOpen={isOpen}
        title="Report settings"
        body={body}
        buttonText={isDownloading ? 'Generating...' : 'Download report'}
        buttonCancelText="Cancel"
        onButtonClick={this.onDownloadClick}
        onCloseClick={onCancelClick}
        onCloseIconClick={onCancelClick}
        customClass="subscriptionReportModal"
        isBodyBold={false}
      />
    );
  }
}
